/**
 * A gust of wind: a flurry of leaves blows across the window from off the
 * left edge to off the right, fluttering, spinning and flipping over as it
 * goes, with a few faint curls of wind among them. Small, faint, slower
 * leaves behind the rest give it depth.
 */
import { calm, pick, rand, scene } from "./scene";

const TAU = Math.PI * 2;

/** How many leaves blow past, and the share of them that are far off. */
const LEAVES = 70;
const CALM_LEAVES = 20;
const FAR_SHARE = 0.35;
/** Leaves enter over this long, in seconds, bunched towards the start like the front of a gust. */
const ENTRY_S = 1.5;
const CALM_ENTRY_S = 1;
/** How long leaves take to cross, in seconds; calm ones drift over slower. The entry plus the slowest crossing is the emote's length, about 5 s. */
const NEAR_CROSS: [number, number] = [2.2, 3.2];
const FAR_CROSS: [number, number] = [3.2, 3.6];
const CALM_NEAR_CROSS: [number, number] = [3.2, 3.8];
const CALM_FAR_CROSS: [number, number] = [3.6, 4];
/** How much the gust dies down as it goes: 0.3 is 1.3x the average speed going in and 0.7x coming out. */
const EASE = 0.3;
const CALM_EASE = 0.15;
/** The share of near leaves an eddy throws round a loop. Its radius scales with the width, to outpace the crossing on any screen, within these px. */
const LOOPS = 0.12;
const LOOP_MIN = 28;
const LOOP_MAX = 90;
/** The current the whole flurry rides, as a share of the window's height, so the leaves rise and fall together a little. */
const CURRENT = 0.035;
const CALM_CURRENT = 0.015;
/** How thin a leaf gets edge on, so it reads as a sliver rather than vanishing. */
const EDGE_ON = 0.12;
/** Line widths at unit leaf length, so they scale with the leaf. */
const VEIN_W = 0.03;
const STEM_W = 0.045;

const STREAKS = 5;
const CALM_STREAKS = 2;
/** How much of a streak's path shows at once, as a share of it. */
const TAIL = 0.45;
/** A streak's curl: how far round it goes, and how much tighter it is by the end. */
const CURL_TURN = 2.2 * Math.PI;
const CURL_TIGHTEN = 0.55;
/** Segments along the visible part of a streak. */
const STREAK_STEPS = 36;

interface Palette {
  greens: string[];
  autumn: string[];
  /** The wind streaks: benleaf's green, a touch stronger on a dark page so they show as faintly as on a light one. */
  wind: string;
  windAlpha: number;
}

/** Each theme's colours, bright or deep enough to stand out on its page: mostly greens around benleaf's own, some autumn. */
const PALETTES: Record<"light" | "dark", Palette> = {
  light: {
    greens: ["#1d5a3f", "#2e7d4f", "#4c9a52", "#73a942", "#3b8a6b"],
    autumn: ["#e3a21a", "#dd7a22", "#c4492c", "#b98b22"],
    wind: "#1d5a3f",
    windAlpha: 1,
  },
  dark: {
    greens: ["#7fcca0", "#5fb67e", "#8fcf6a", "#3f9a66", "#a6d98c"],
    autumn: ["#f2c446", "#f0913a", "#e9673f", "#d9a63a"],
    wind: "#7fcca0",
    windAlpha: 1.5,
  },
};
const AUTUMN_SHARE = 0.3;

/** A leaf outline and its markings at unit length, stem end at x = -0.5 and tip at 0.5. */
interface Shape {
  blade: Path2D;
  veins: Path2D;
  stem: Path2D;
}

interface Leaf {
  /** When it enters and how long it takes to cross, in seconds. */
  delay: number;
  span: number;
  /** Length in px. */
  size: number;
  shape: Shape;
  alpha: number;
  /** 1 near, 0.5 far: far leaves seem to move about less. */
  depth: number;
  /** Height it enters at, as a share of the window, and how far it rises (-) or sinks over the crossing. */
  y: number;
  drift: number;
  /** The slow swell it rides across the window: px, cycles per crossing, phase. */
  swell: number;
  swellCycles: number;
  swellPhase: number;
  /** The quick flutter up and down: px, Hz, phase. It tips nose up as it rises, by `tilt` radians. */
  bob: number;
  bobHz: number;
  bobPhase: number;
  tilt: number;
  /** Gusty surges back and forth: px, Hz, phase. */
  surge: number;
  surgeHz: number;
  surgePhase: number;
  /** Starting angle and how many turns it spins over the crossing, so it spins slower as the gust dies. */
  angle: number;
  turns: number;
  /** The 3D flip: about its long axis (squashing its width) or its short one (squashing its length). */
  flipHz: number;
  flipPhase: number;
  flipLong: boolean;
  top: string;
  under: string;
  vein: string;
  underVein: string;
  stem: string;
  /** The eddy's loop: radius as a share of the width (0 for none), when it starts and how long it lasts, as shares of the crossing. */
  loop: number;
  loopAt: number;
  loopFor: number;
}

interface Streak {
  start: number;
  span: number;
  /** Where its run starts, as shares of the window, and the run's length, as a share of the width. */
  x: number;
  y: number;
  run: number;
  /** How far the run bows down (or up), the curl's radius and the streak's width at its thickest, in px. */
  sag: number;
  curl: number;
  width: number;
  alpha: number;
}

/** `hex` taken a share `k` of the way to white (k > 0) or black (k < 0). */
function shade(hex: string, k: number) {
  const n = parseInt(hex.slice(1), 16);
  const to = k > 0 ? 255 : 0;
  const ch = (v: number) => Math.round(v + (to - v) * Math.abs(k));
  return `rgb(${ch(n >> 16)}, ${ch((n >> 8) & 255)}, ${ch(n & 255)})`;
}

/** Midrib, three pairs of side veins that stay inside a blade `hw` wide at its widest, and a short curved stem. */
function marked(blade: Path2D, hw: number): Shape {
  const veins = new Path2D();
  veins.moveTo(-0.5, 0);
  veins.lineTo(0.44, 0);
  for (const x of [-0.28, -0.08, 0.12]) {
    const end = x + 0.16;
    const reach = hw * 0.6 * (1 - 4 * end * end);
    veins.moveTo(x, 0);
    veins.lineTo(end, -reach);
    veins.moveTo(x, 0);
    veins.lineTo(end, reach);
  }
  const stem = new Path2D();
  stem.moveTo(-0.5, 0);
  stem.quadraticCurveTo(-0.58, 0, -0.66, 0.05);
  return { blade, veins, stem };
}

/** A pointed oval, a narrow, slightly curved willow leaf, and a broad birch leaf with a drip tip. */
function shapes(): Shape[] {
  const oval = new Path2D();
  oval.moveTo(-0.5, 0);
  oval.bezierCurveTo(-0.36, -0.3, 0.04, -0.33, 0.28, -0.14);
  oval.quadraticCurveTo(0.42, -0.03, 0.5, 0);
  oval.quadraticCurveTo(0.42, 0.03, 0.28, 0.14);
  oval.bezierCurveTo(0.04, 0.33, -0.36, 0.3, -0.5, 0);

  const willow = new Path2D();
  willow.moveTo(-0.5, 0);
  willow.bezierCurveTo(-0.3, -0.17, 0.15, -0.15, 0.5, 0);
  willow.bezierCurveTo(0.15, 0.1, -0.3, 0.2, -0.5, 0);

  const birch = new Path2D();
  birch.moveTo(-0.5, 0);
  birch.bezierCurveTo(-0.55, -0.38, 0, -0.37, 0.3, -0.12);
  birch.quadraticCurveTo(0.4, -0.04, 0.5, 0);
  birch.quadraticCurveTo(0.4, 0.04, 0.3, 0.12);
  birch.bezierCurveTo(0, 0.37, -0.55, 0.38, -0.5, 0);

  return [marked(oval, 0.25), marked(willow, 0.12), marked(birch, 0.29)];
}

function makeLeaf(far: boolean, quiet: boolean, palette: Palette, kinds: Shape[]): Leaf {
  const depth = far ? 0.5 : 1;
  const gentle = quiet ? 0.5 : 1;
  const top = pick(Math.random() < AUTUMN_SHARE ? palette.autumn : palette.greens);
  const under = shade(top, -0.3);
  const span = quiet ? rand(...(far ? CALM_FAR_CROSS : CALM_NEAR_CROSS)) : rand(...(far ? FAR_CROSS : NEAR_CROSS));
  return {
    delay: (quiet ? CALM_ENTRY_S : ENTRY_S) * Math.random() ** 1.4,
    span,
    size: far ? rand(11, 18) : rand(24, 40),
    shape: pick(kinds),
    alpha: far ? rand(0.3, 0.5) : rand(0.85, 1),
    depth,
    y: rand(0.02, 0.98),
    drift: rand(-0.08, 0.06) * gentle,
    swell: rand(16, 48) * depth * gentle,
    swellCycles: rand(0.5, 1.3),
    swellPhase: rand(0, TAU),
    bob: rand(6, 18) * depth * gentle,
    bobHz: rand(0.8, 1.7) * (quiet ? 0.6 : 1),
    bobPhase: rand(0, TAU),
    tilt: rand(0.25, 0.6) * gentle,
    surge: quiet ? 0 : rand(4, 12) * depth,
    surgeHz: rand(0.6, 1.3),
    surgePhase: rand(0, TAU),
    angle: rand(0, TAU),
    turns: (Math.random() < 0.7 ? 1 : -1) * rand(0.5, 2) * (quiet ? 0.35 : 1),
    flipHz: rand(0.3, 1.1) * gentle,
    flipPhase: rand(0, TAU),
    flipLong: Math.random() < 0.6,
    top,
    under,
    vein: shade(top, 0.35),
    underVein: shade(top, 0.1),
    stem: under,
    loop: !far && !quiet && Math.random() < LOOPS ? rand(0.035, 0.05) : 0,
    loopAt: rand(0.25, 0.5),
    loopFor: rand(0.55, 0.75) / span,
  };
}

function makeStreak(i: number, count: number, quiet: boolean, strength: number): Streak {
  const run = rand(0.22, 0.38);
  return {
    start: (i / count) * 1.8 + rand(0, 0.3),
    span: rand(1.5, 2) * (quiet ? 1.15 : 1),
    x: rand(0.02, 0.85 - run),
    y: rand(0.12, 0.88),
    run,
    sag: rand(-16, 16),
    curl: rand(16, 30),
    width: rand(1.6, 2.6),
    alpha: rand(0.14, 0.22) * strength,
  };
}

function drawLeaf(ctx: CanvasRenderingContext2D, l: Leaf, s: number, w: number, h: number, ease: number, current: number) {
  const age = s - l.delay;
  const u = age / l.span;
  if (u <= 0 || u >= 1) return;
  // Off the left edge at the start and off the right at the end, whatever it's doing in between.
  const margin = l.size + 24;
  const p = u + ease * u * (1 - u);
  let x = (w + 2 * margin) * p - margin + l.surge * Math.sin(l.surgePhase + TAU * l.surgeHz * age);
  const bob = l.bobPhase + TAU * l.bobHz * age;
  let y =
    (l.y + l.drift * p) * h +
    l.swell * Math.sin(l.swellPhase + TAU * l.swellCycles * p) +
    l.bob * Math.sin(bob) +
    current * l.depth * h * Math.sin((TAU * 0.8 * x) / w - 1.6 * s);
  let angle = l.angle + TAU * l.turns * p + l.tilt * Math.cos(bob);

  if (l.loop) {
    const q = (u - l.loopAt) / l.loopFor;
    if (q > 0 && q < 1) {
      // Forward, up, back over the top and down again, eased at both ends so it joins the flight smoothly.
      const r = Math.min(LOOP_MAX, Math.max(LOOP_MIN, l.loop * w));
      const a = TAU * q * q * (3 - 2 * q);
      x += r * Math.sin(a);
      y -= r * (1 - Math.cos(a));
      angle -= a;
    }
  }

  const c = Math.cos(l.flipPhase + TAU * l.flipHz * age);
  const squash = c < 0 ? Math.min(c, -EDGE_ON) : Math.max(c, EDGE_ON);
  ctx.save();
  ctx.globalAlpha = l.alpha;
  ctx.translate(x, y);
  ctx.rotate(angle);
  if (l.flipLong) ctx.scale(l.size, l.size * squash);
  else ctx.scale(l.size * squash, l.size);
  ctx.fillStyle = squash < 0 ? l.under : l.top;
  ctx.fill(l.shape.blade);
  ctx.lineWidth = VEIN_W;
  ctx.strokeStyle = squash < 0 ? l.underVein : l.vein;
  ctx.stroke(l.shape.veins);
  ctx.lineWidth = STEM_W;
  ctx.strokeStyle = l.stem;
  ctx.stroke(l.shape.stem);
  ctx.restore();
}

/** Where a streak's path is at `at` (0 to 1), into `out` at `i`: a long, gently bowed run that ends curling up and inwards. */
function streakAt(k: Streak, at: number, w: number, h: number, out: Float32Array, i: number) {
  const x0 = k.x * w;
  const y0 = k.y * h;
  const run = k.run * w;
  // Split by rough length, so the streak keeps its pace going into the curl.
  const split = run / (run + CURL_TURN * k.curl * (1 - CURL_TIGHTEN / 2));
  if (at <= split) {
    const q = at / split;
    out[i] = x0 + run * q;
    out[i + 1] = y0 + k.sag * Math.sin(Math.PI * q) ** 2;
  } else {
    const a = ((at - split) / (1 - split)) * CURL_TURN;
    const r = k.curl * (1 - (CURL_TIGHTEN * a) / CURL_TURN);
    out[i] = x0 + run + r * Math.sin(a);
    out[i + 1] = y0 - k.curl + r * Math.cos(a);
  }
}

/** A streak as a thin ribbon, pointed at both ends and thickest towards its head, sliding along its path. */
function drawStreak(ctx: CanvasRenderingContext2D, k: Streak, s: number, w: number, h: number, pts: Float32Array, offs: Float32Array) {
  const q = (s - k.start) / k.span;
  if (q <= 0 || q >= 1) return;
  const head = (1 + TAIL) * (1 - (1 - q) ** 1.7);
  const from = Math.max(0, head - TAIL);
  const to = Math.min(1, head);
  if (to - from < 0.01) return;
  const step = (to - from) / STREAK_STEPS;
  for (let i = 0; i <= STREAK_STEPS; i++) streakAt(k, from + step * i, w, h, pts, 2 * i);
  for (let i = 0; i <= STREAK_STEPS; i++) {
    const a = 2 * Math.max(0, i - 1);
    const b = 2 * Math.min(STREAK_STEPS, i + 1);
    const tx = pts[b] - pts[a];
    const ty = pts[b + 1] - pts[a + 1];
    const len = Math.hypot(tx, ty) || 1;
    const at = from + step * i;
    const along = (at - head + TAIL) / TAIL;
    const half = (k.width / 2) * Math.sin(Math.PI * along * along) * Math.min(1, at * 12, (1 - at) * 12);
    offs[2 * i] = (-ty / len) * half;
    offs[2 * i + 1] = (tx / len) * half;
  }
  ctx.globalAlpha = k.alpha * Math.min(1, q / 0.15, (1 - q) / 0.3);
  ctx.beginPath();
  ctx.moveTo(pts[0] + offs[0], pts[1] + offs[1]);
  for (let i = 1; i <= STREAK_STEPS; i++) ctx.lineTo(pts[2 * i] + offs[2 * i], pts[2 * i + 1] + offs[2 * i + 1]);
  for (let i = STREAK_STEPS; i >= 0; i--) ctx.lineTo(pts[2 * i] - offs[2 * i], pts[2 * i + 1] - offs[2 * i + 1]);
  ctx.closePath();
  ctx.fill();
}

export default function play(): Promise<void> {
  const quiet = calm();
  const palette = PALETTES[document.documentElement.dataset.theme === "dark" ? "dark" : "light"];
  const kinds = shapes();
  const count = quiet ? CALM_LEAVES : LEAVES;
  const farCount = Math.round(count * FAR_SHARE);
  const far = Array.from({ length: farCount }, () => makeLeaf(true, quiet, palette, kinds));
  // Bigger leaves are nearer, so they pass in front.
  const near = Array.from({ length: count - farCount }, () => makeLeaf(false, quiet, palette, kinds)).sort((a, b) => a.size - b.size);
  const streakCount = quiet ? CALM_STREAKS : STREAKS;
  const streaks = Array.from({ length: streakCount }, (_, i) => makeStreak(i, streakCount, quiet, palette.windAlpha));
  const ease = quiet ? CALM_EASE : EASE;
  const current = quiet ? CALM_CURRENT : CURRENT;
  const end = Math.max(...far.map((l) => l.delay + l.span), ...near.map((l) => l.delay + l.span), ...streaks.map((k) => k.start + k.span));
  const pts = new Float32Array(2 * (STREAK_STEPS + 1));
  const offs = new Float32Array(2 * (STREAK_STEPS + 1));

  return scene(({ ctx, w, h, t }) => {
    const s = t / 1000;
    ctx.lineCap = "round";
    for (const l of far) drawLeaf(ctx, l, s, w, h, ease, current);
    ctx.fillStyle = palette.wind;
    for (const k of streaks) drawStreak(ctx, k, s, w, h, pts, offs);
    ctx.globalAlpha = 1;
    for (const l of near) drawLeaf(ctx, l, s, w, h, ease, current);
    return s < end;
  });
}
